<template>
  <div class="company-details">
    <template v-if="bonusesPage">
      <CompanyBonusesPage
        :company-id="companyId"
        :company-name="companyMeta.name"
        :company-city="companyMeta.city"
        :employees="employees"
        @close="bonusesPage = false"
        @refresh-employees="fetchEmployees"
      />
    </template>
    <template v-else>
      <v-row class="details-grid" dense>
        <v-col cols="12" sm="6" md="4" lg="4" class="details-grid__col">
          <v-card class="details-card" outlined @click="notifyOpenEmployees">
            <v-card-title class="details-card__title">
              {{ $t("companyDetailsPage.cards.employees.title") }}
            </v-card-title>
            <v-card-text class="details-card__text">
              {{ $t("companyDetailsPage.cards.employees.desc") }}
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="details-grid__col">
          <v-card class="details-card" outlined @click="notifyOpenChecklist">
            <v-card-title class="details-card__title">
              {{ $t("companyDetailsPage.cards.checklist.title") }}
            </v-card-title>
            <v-card-text class="details-card__text">
              <div class="counts-row">
                <v-chip
                  small
                  class="counts-chip counts-chip--total"
                  outlined
                >
                  <v-icon left size="16">mdi-format-list-bulleted</v-icon>
                  <span class="counts-label">{{ $t("companyDetailsPage.cards.checklist.total") }}</span>
                  <span class="counts-value">
                    <span v-if="checklistCounts.total !== null">{{ checklistCounts.total }}</span>
                    <span v-else>—</span>
                  </span>
                </v-chip>
                <v-chip
                  small
                  class="counts-chip counts-chip--overdue"
                  :class="{ 'counts-chip--overdue-zero': (checklistCounts.overdue || 0) === 0 }"
                  outlined
                >
                  <v-icon left size="16">mdi-alert-circle</v-icon>
                  <span class="counts-label">{{ $t("companyDetailsPage.cards.checklist.overdue") }}</span>
                  <span class="counts-value">
                    <span v-if="checklistCounts.overdue !== null">{{ checklistCounts.overdue }}</span>
                    <span v-else>—</span>
                  </span>
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="details-grid__col">
          <v-card class="details-card" outlined @click="openBonusesPage">
            <v-card-title class="details-card__title">
              {{ $t("companyDetailsPage.cards.bonuses.title") }}
            </v-card-title>
            <v-card-text class="details-card__text">
              {{ $t("companyDetailsPage.cards.bonuses.desc") }}
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="details-grid__col">
          <v-card
            class="details-card"
            :class="{ 'details-card--loading': employeesLoading }"
            outlined
            @click="handleBulkCardClick"
          >
            <v-card-title class="details-card__title">
              {{ $t("companyDetailsPage.cards.bulk.title") }}
            </v-card-title>
            <v-card-text class="details-card__text">
              {{ $t("companyDetailsPage.cards.bulk.desc") }}
            </v-card-text>
            <v-progress-linear
              v-if="employeesLoading"
              indeterminate
              color="primary"
              class="details-card__loader"
            ></v-progress-linear>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="details-grid__col">
          <v-card
            class="details-card"
            :class="{ 'details-card--loading': employeesLoading }"
            outlined
            @click="handleDownloadCardClick"
          >
            <v-card-title class="details-card__title">
              {{ $t("companyDetailsPage.cards.download.title") }}
            </v-card-title>
            <v-card-text class="details-card__text">
              {{ $t("companyDetailsPage.cards.download.desc") }}
            </v-card-text>
            <v-progress-linear
              v-if="employeesLoading"
              indeterminate
              color="primary"
              class="details-card__loader"
            ></v-progress-linear>
          </v-card>
        </v-col>
        <v-col
          v-if="allowCompanyEdit"
          cols="12"
          sm="6"
          md="4"
          lg="4"
          class="details-grid__col"
        >
          <v-card class="details-card" outlined @click="openCompanyEditDialog">
            <v-card-title class="details-card__title">
              {{ $t("companyDetailsPage.cards.edit.title") }}
            </v-card-title>
            <v-card-text class="details-card__text d-flex flex-column ga-3">
              <div class="d-flex align-center justify-space-between flex-wrap ga-2 settings-row">
                <div class="text-subtitle-2 d-flex align-center ga-2">
                  <span>{{ $t("companyDetailsPage.cards.edit.package") }}</span>
                </div>
                <v-chip color="primary">
                  {{ currentLicensePackageLabel }}
                </v-chip>
              </div>
              <div class="d-flex align-center justify-space-between flex-wrap ga-2 settings-row">
                <div class="text-subtitle-2 d-flex align-center ga-2">
                  <span>{{ $t("companyDetailsPage.cards.edit.expiresAt") }}</span>
                </div>
                <span class="text-body-2">{{ currentLicenseExpiryLabel }}</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="4" class="details-grid__col">
          <v-card class="details-card" outlined @click="openSettingsDialog" style="cursor: pointer;">
            <v-card-title class="details-card__title">
              {{ $t("companyDetailsPage.cards.settings.title") }}
            </v-card-title>
            <v-card-text class="details-card__text d-flex flex-column ga-3">
              <div class="d-flex align-center justify-space-between flex-wrap ga-2 settings-row">
                <div class="text-subtitle-2 d-flex align-center ga-2">
                  <span>{{ $t("companyDetailsPage.cards.settings.requests") }} </span>
                </div>
                <v-chip
                  :color="companySettings.pdfrequest ? 'warning' : 'success'"
                >
                  {{ companySettings.pdfrequest ? $t("companyDetailsPage.cards.settings.off") : $t("companyDetailsPage.cards.settings.on") }}
                </v-chip>
              </div>
              <div class="d-flex align-center justify-space-between flex-wrap ga-2 settings-row">
                <div class="text-subtitle-2 d-flex align-center ga-2">
                  <span>{{ $t("companyDetailsPage.cards.settings.emails") }}</span>
                </div>
                <v-chip
                  :color="companySettings.email_notifications_disabled ? 'warning' : 'success'"
                >
                  {{ companySettings.email_notifications_disabled ? $t("companyDetailsPage.cards.settings.off") : $t("companyDetailsPage.cards.settings.on") }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-dialog v-model="settingsDialog" max-width="420px">
      <v-card>
        <v-card-title>{{ $t("companyDetailsPage.settingsDialog.title") }}</v-card-title>
        <v-card-text>
          <v-checkbox
            v-model="companySettings.pdfrequest"
            :label="$t('companyDetailsPage.settingsDialog.hideRequests')"
            density="comfortable"
            hide-details
          ></v-checkbox>
          <v-checkbox
            v-model="companySettings.email_notifications_disabled"
            :label="$t('companyDetailsPage.settingsDialog.disableEmails')"
            density="comfortable"
            hide-details
          ></v-checkbox>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="settingsDialog = false">{{ $t("app.actions.cancel") }}</v-btn>
          <v-btn color="primary" :loading="companySettings.loading" @click="saveCompanySettings">{{ $t("app.actions.save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <CompanyFormDialog
      v-model="companyEditDialog"
      :is-edit="true"
      :form-value="companyProfileForm"
      :status-items="statusItems"
      :license-package-items="licensePackageItems"
      :license-packages-loading="licensePackagesLoading"
      :save-loading="companyEditLoading"
      :error-message="companyEditError"
      @save="saveCompanyProfile"
    />

    <v-dialog v-model="bulkTimesheetDialog.open" max-width="540px">
      <v-card>
        <v-card-title>{{ $t("companyDetailsPage.bulkDialog.title") }}</v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3">
            {{ $t("companyDetailsPage.bulkDialog.description") }}
          </p>
          <v-alert
            v-if="bulkTimesheetDialog.error"
            type="error"
            density="comfortable"
            class="mb-3"
          >
            {{ bulkTimesheetDialog.error }}
          </v-alert>

          <v-menu
            v-model="bulkTimesheetRangeMenu"
            :close-on-content-click="false"
            max-width="360"
            transition="scale-transition"
          >
            <template #activator="{ props: menuProps }">
              <div class="d-flex ga-2 flex-wrap w-100" v-bind="menuProps">
            <v-text-field
              class="flex-grow-1"
              :label="$t('companyDetailsPage.bulkDialog.from')"
              outlined
              readonly
              :model-value="bulkTimesheetRangeDisplay.start"
              @click.stop="handleBulkTimesheetControlClick('start', $event, menuProps)"
              @mousedown="handleBulkTimesheetPointerDown('start')"
              @focus="handleBulkTimesheetFieldFocus('start')"
              @keydown.enter.prevent="handleBulkTimesheetKeyboardOpen('start')"
              @keydown.space.prevent="handleBulkTimesheetKeyboardOpen('start')"
              @click:control.stop="handleBulkTimesheetControlClick('start', $event, menuProps)"
            ></v-text-field>
            <v-text-field
              class="flex-grow-1"
              :label="$t('companyDetailsPage.bulkDialog.to')"
              outlined
              readonly
              :model-value="bulkTimesheetRangeDisplay.end"
              @click.stop="handleBulkTimesheetControlClick('end', $event, menuProps)"
              @mousedown="handleBulkTimesheetPointerDown('end')"
              @focus="handleBulkTimesheetFieldFocus('end')"
              @keydown.enter.prevent="handleBulkTimesheetKeyboardOpen('end')"
              @keydown.space.prevent="handleBulkTimesheetKeyboardOpen('end')"
              @click:control.stop="handleBulkTimesheetControlClick('end', $event, menuProps)"
            ></v-text-field>
              </div>
            </template>
            <v-card>
              <v-date-picker
                :model-value="bulkTimesheetRangeDateModel"
                color="primary"
                :first-day-of-week="1"
                show-adjacent-months
                range
                @update:model-value="onBulkTimesheetRangeChange"
              ></v-date-picker>
              <v-card-actions class="justify-end">
                <v-btn variant="text" @click="bulkTimesheetRangeMenu = false">{{ $t("app.actions.cancel") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>

          <div class="mt-4">
            <div class="d-flex align-center justify-space-between mb-1">
              <span class="text-subtitle-2">Angajați</span>
              <v-checkbox
                v-model="selectAllEmployees"
                label="Selectează toți"
                density="compact"
                hide-details
              ></v-checkbox>
            </div>
            <v-alert
              v-if="!employees.length"
              type="info"
              density="comfortable"
            >
              Nu există angajați disponibili pentru această companie.
            </v-alert>
            <v-list
              v-else
              class="employee-list"
              density="compact"
            >
              <v-list-item
                v-for="employee in employees"
                :key="employee.id"
              >
                <template #prepend>
                  <v-checkbox
                    v-model="selectedEmployeeIds"
                    :value="employee.id"
                    hide-details
                    density="compact"
                  ></v-checkbox>
                </template>
                <v-list-item-title>{{ employee.first_name }} {{ employee.last_name }}</v-list-item-title>
                <v-list-item-subtitle v-if="employee.position">
                  {{ employee.position }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </div>

          <div v-if="bulkTimesheetDialog.results.length" class="mt-4">
            <span class="text-subtitle-2">Rezultate</span>
            <v-list density="compact">
              <v-list-item
                v-for="result in bulkTimesheetDialog.results"
                :key="result.id"
              >
                <v-list-item-title>
                  {{ result.name }}
                </v-list-item-title>
                <v-list-item-subtitle :class="result.success ? 'text-success' : 'text-error'">
                  {{ result.message }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            :disabled="bulkTimesheetDialog.loading"
            @click="bulkTimesheetDialog.open = false"
          >
            Închide
          </v-btn>
          <v-btn
            color="primary"
            :loading="bulkTimesheetDialog.loading"
            :disabled="!employees.length"
            @click="confirmBulkTimesheetFill"
          >
            Completează
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="bulkDownloadDialog.open" max-width="640px">
      <v-card>
        <v-card-title>{{ $t("companyDetailsPage.bulkDownloadDialog.title") }}</v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3">
            {{ $t("companyDetailsPage.bulkDownloadDialog.description") }}
          </p>
          <v-alert
            v-if="bulkDownloadDialog.error"
            type="error"
            density="comfortable"
            class="mb-3"
          >
            {{ bulkDownloadDialog.error }}
          </v-alert>
          <v-alert
            v-if="bulkDownloadDialog.notice"
            type="info"
            density="comfortable"
            class="mb-3"
          >
            {{ bulkDownloadDialog.notice }}
          </v-alert>

          <v-menu
            v-model="bulkDownloadRangeMenu"
            :close-on-content-click="false"
            max-width="360"
            transition="scale-transition"
          >
            <template #activator="{ props: menuProps }">
              <div class="d-flex ga-2 flex-wrap w-100" v-bind="menuProps">
                <v-text-field
                  class="flex-grow-1"
                  :label="$t('companyDetailsPage.bulkDownloadDialog.from')"
                  outlined
                  readonly
                  :model-value="bulkDownloadRangeDisplay.start"
                  @click.stop="handleBulkDownloadControlClick('start', $event, menuProps)"
                  @mousedown="handleBulkDownloadPointerDown('start')"
                  @focus="handleBulkDownloadFieldFocus('start')"
                  @keydown.enter.prevent="handleBulkDownloadKeyboardOpen('start')"
                  @keydown.space.prevent="handleBulkDownloadKeyboardOpen('start')"
                  @click:control.stop="handleBulkDownloadControlClick('start', $event, menuProps)"
                ></v-text-field>
                <v-text-field
                  class="flex-grow-1"
                  :label="$t('companyDetailsPage.bulkDownloadDialog.to')"
                  outlined
                  readonly
                  :model-value="bulkDownloadRangeDisplay.end"
                  @click.stop="handleBulkDownloadControlClick('end', $event, menuProps)"
                  @mousedown="handleBulkDownloadPointerDown('end')"
                  @focus="handleBulkDownloadFieldFocus('end')"
                  @keydown.enter.prevent="handleBulkDownloadKeyboardOpen('end')"
                  @keydown.space.prevent="handleBulkDownloadKeyboardOpen('end')"
                  @click:control.stop="handleBulkDownloadControlClick('end', $event, menuProps)"
                ></v-text-field>
              </div>
            </template>
            <v-card>
              <v-date-picker
                :model-value="bulkDownloadRangeDateModel"
                color="primary"
                :first-day-of-week="1"
                show-adjacent-months
                range
                @update:model-value="onBulkDownloadRangeChange"
              ></v-date-picker>
              <v-card-actions class="justify-end">
                <v-btn variant="text" @click="bulkDownloadRangeMenu = false">{{ $t("app.actions.cancel") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>

          <div class="mt-4">
            <div class="d-flex align-center justify-space-between mb-1">
              <span class="text-subtitle-2">{{ $t("companyDetailsPage.bulkDownloadDialog.employees") }}</span>
              <v-checkbox
                v-model="downloadSelectAll"
                :label="$t('companyDetailsPage.bulkDownloadDialog.selectAll')"
                density="compact"
                hide-details
              ></v-checkbox>
            </div>
            <v-alert
              v-if="!employees.length"
              type="info"
              density="comfortable"
            >
              {{ $t("companyDetailsPage.bulkDownloadDialog.noEmployees") }}
            </v-alert>
            <v-list
              v-else
              class="employee-list"
              density="compact"
            >
              <v-list-item
                v-for="employee in employees"
                :key="employee.id"
              >
                <template #prepend>
                  <v-checkbox
                    v-model="selectedDownloadEmployeeIds"
                    :value="employee.id"
                    hide-details
                    density="compact"
                  ></v-checkbox>
                </template>
                <v-list-item-title>{{ employee.first_name }} {{ employee.last_name }}</v-list-item-title>
                <v-list-item-subtitle v-if="employee.position">
                  {{ employee.position }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </div>

          <div class="mt-4">
            <span class="text-subtitle-2">{{ $t("companyDetailsPage.bulkDownloadDialog.format") }}</span>
            <v-radio-group v-model="bulkDownloadDialog.format" class="mt-2" hide-details>
              <v-radio :label="$t('companyDetailsPage.bulkDownloadDialog.pdf')" value="pdf"></v-radio>
              <v-radio :label="$t('companyDetailsPage.bulkDownloadDialog.xlsx')" value="xlsx"></v-radio>
            </v-radio-group>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            :disabled="bulkDownloadDialog.loading"
            @click="bulkDownloadDialog.open = false"
          >
            {{ $t("app.actions.cancel") }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="bulkDownloadDialog.loading"
            :disabled="!employees.length"
            @click="confirmBulkDownload"
          >
            {{ $t("companyDetailsPage.bulkDownloadDialog.download") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="statusSnackbar.open"
      :color="statusSnackbar.color"
      timeout="6000"
      multi-line
    >
      {{ statusSnackbar.message }}
      <template #actions>
        <v-btn color="white" variant="text" @click="statusSnackbar.open = false">
          Închide
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";
import { formatAsIsoDate, toDateObject } from "@/Utile/holidayFormatting.mjs";
import CompanyBonusesPage from "@/components/payroll/CompanyBonusesPage.vue";
import CompanyFormDialog from "@/components/CompanyFormDialog.vue";
import {
  DEFAULT_COMPANY_LICENSE_PACKAGE_KEY,
  buildCompanyForm,
  buildCompanyPayload,
  buildLicensePackageSelectItems,
  buildLicensePayload,
  createEmptyCompanyForm,
  formatCompanyLicenseExpiry,
  resolveLicensePackageLabelByKey,
} from "@/Utile/companyForm.mjs";
import { normalizeRangeSelection, buildDefaultFillRange } from "@/Utile/calendarRange.mjs";

export default defineComponent({
  name: "CompanyDetails",
  components: { CompanyBonusesPage, CompanyFormDialog },
  props: {
    companyId: {
      type: Number,
      required: true,
    },
    allowCompanyEdit: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["open-employees", "open-checklist", "company-updated"],
  data() {
    return {
      statusItems: [
        { title: this.$t("companies.values.active"), value: 1 },
        { title: this.$t("companies.values.inactive"), value: 0 },
      ],
      employees: [],
      employeesLoading: false,
      selectedEmployeeIds: [],
      bulkTimesheetDialog: {
        open: false,
        loading: false,
        error: null,
        start_date: "",
        end_date: "",
        results: [],
      },
      bulkDownloadDialog: {
        open: false,
        loading: false,
        error: null,
        notice: "",
        start_date: "",
        end_date: "",
        format: "pdf",
      },
      bulkTimesheetRangeMenu: false,
      bulkTimesheetActiveEdge: "start",
      selectAllEmployees: true,
      selectedDownloadEmployeeIds: [],
      downloadSelectAll: true,
      bulkDownloadRangeMenu: false,
      bulkDownloadActiveEdge: "start",
      statusSnackbar: {
        open: false,
        color: "success",
        message: "",
      },
      companySettings: {
        pdfrequest: false,
        email_notifications_disabled: false,
        loading: false,
      },
      settingsDialog: false,
      companyEditDialog: false,
      companyEditLoading: false,
      companyEditError: "",
      availableLicensePackages: [],
      licensePackagesLoading: false,
      companyProfileForm: createEmptyCompanyForm(),
      companyLicense: null,
      checklistCounts: {
        loading: false,
        total: null,
        overdue: null,
      },
      bonusesPage: false,
      companyMeta: {
        name: "",
        city: "",
      },
    };
  },
  computed: {
    bulkTimesheetRangeDisplay() {
      return {
        start: this.bulkTimesheetDialog.start_date || "",
        end: this.bulkTimesheetDialog.end_date || "",
      };
    },
    bulkTimesheetRangeDateModel() {
      const start = toDateObject(this.bulkTimesheetDialog.start_date);
      const end = toDateObject(this.bulkTimesheetDialog.end_date);

      if (start && end) {
        if (start <= end) {
          return [start, end];
        }
        return [end, start];
      }
      if (start) {
        return [start];
      }
      return [];
    },
    bulkDownloadRangeDisplay() {
      return {
        start: this.bulkDownloadDialog.start_date || "",
        end: this.bulkDownloadDialog.end_date || "",
      };
    },
    bulkDownloadRangeDateModel() {
      const start = toDateObject(this.bulkDownloadDialog.start_date);
      const end = toDateObject(this.bulkDownloadDialog.end_date);

      if (start && end) {
        if (start <= end) {
          return [start, end];
        }
        return [end, start];
      }
      if (start) {
        return [start];
      }
      return [];
    },
    licensePackageItems() {
      return buildLicensePackageSelectItems(
        this.availableLicensePackages,
        this.$i18n?.locale || "ro"
      );
    },
    currentLicensePackageLabel() {
      const packageKey =
        this.companyLicense?.package_key ||
        this.companyProfileForm?.license_package_key ||
        this.getDefaultLicensePackageKey();
      return (
        resolveLicensePackageLabelByKey(
          this.availableLicensePackages,
          packageKey,
          this.$i18n?.locale || "ro"
        ) ||
        this.$t("companyDetailsPage.cards.edit.noPackage")
      );
    },
    currentLicenseExpiryLabel() {
      return formatCompanyLicenseExpiry(
        this.companyLicense?.expires_at,
        this.$i18n?.locale || "ro",
        this.$t("companyDetailsPage.cards.edit.noExpiry")
      );
    },
  },
  watch: {
    companyId: {
      immediate: true,
      handler(newCompanyId) {
        if (!newCompanyId) return;
        this.fetchEmployees();
        this.fetchCompanySettings();
        this.fetchChecklistCounts();
      },
    },
    selectAllEmployees(newValue) {
      if (newValue) {
        this.selectedEmployeeIds = this.employees.map((employee) => employee.id);
      } else if (this.selectedEmployeeIds.length === this.employees.length) {
        this.selectedEmployeeIds = [];
      }
    },
    employees: {
      immediate: true,
      handler(list) {
        if (this.selectAllEmployees) {
          this.selectedEmployeeIds = list.map((employee) => employee.id);
        }
        if (this.downloadSelectAll) {
          this.selectedDownloadEmployeeIds = list.map((employee) => employee.id);
        }
      },
    },
    selectedEmployeeIds(newSelection) {
      if (!this.employees.length) {
        this.selectAllEmployees = false;
        return;
      }
      const allIds = this.employees.map((employee) => employee.id);
      const hasAll = allIds.every((id) => newSelection.includes(id));
      if (hasAll && newSelection.length === allIds.length) {
        this.selectAllEmployees = true;
      } else if (this.selectAllEmployees) {
        this.selectAllEmployees = false;
      }
    },
    downloadSelectAll(newValue) {
      if (newValue) {
        this.selectedDownloadEmployeeIds = this.employees.map((employee) => employee.id);
      } else if (this.selectedDownloadEmployeeIds.length === this.employees.length) {
        this.selectedDownloadEmployeeIds = [];
      }
    },
    selectedDownloadEmployeeIds(newSelection) {
      if (!this.employees.length) {
        this.downloadSelectAll = false;
        return;
      }
      const allIds = this.employees.map((employee) => employee.id);
      const hasAll = allIds.every((id) => newSelection.includes(id));
      if (hasAll && newSelection.length === allIds.length) {
        this.downloadSelectAll = true;
      } else if (this.downloadSelectAll) {
        this.downloadSelectAll = false;
      }
    },
  },
  methods: {
    async ensureLicensePackagesLoaded(force = false) {
      if (!this.allowCompanyEdit) return;
      if (!force && this.availableLicensePackages.length) return;
      this.licensePackagesLoading = true;
      try {
        const { data } = await axios.get("/license-packages");
        this.availableLicensePackages = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Eroare la încărcarea pachetelor de licență:", error);
        throw error;
      } finally {
        this.licensePackagesLoading = false;
      }
    },
    getDefaultLicensePackageKey() {
      return (
        this.availableLicensePackages.find((pkg) => Number(pkg?.is_default_for_new_companies) === 1)?.key ||
        DEFAULT_COMPANY_LICENSE_PACKAGE_KEY
      );
    },
    applyCompanyProfileData(companyData, licenseData = null) {
      this.companySettings.pdfrequest = Boolean(companyData?.pdfrequest);
      this.companySettings.email_notifications_disabled = Boolean(companyData?.email_notifications_disabled);
      this.companyMeta.name = companyData?.company_name || companyData?.companyName || "";
      const address = companyData?.address || "";
      this.companyMeta.city = address ? String(address).split(",")[0].trim() : "";
      this.companyLicense = licenseData;
      this.companyProfileForm = buildCompanyForm(companyData, licenseData, {
        defaultPackageKey: this.getDefaultLicensePackageKey(),
      });
    },
    notifyOpenEmployees() {
      this.$emit("open-employees", this.companyId);
    },
    notifyOpenChecklist() {
      this.$emit("open-checklist", this.companyId);
    },
    async fetchChecklistCounts() {
      if (!this.companyId) return;
      this.checklistCounts.loading = true;
      try {
        const params = { companyId: this.companyId, includeClosed: true, includeAll: true };
        const { data } = await axios.get("/checklist/tasks", { params });
        const items = data?.items || [];
        const now = new Date();
        const openTasks = items.filter((t) => String(t.status || "").toLowerCase() !== "closed");
        const overdue = openTasks.filter(
          (t) =>
            t.due_date &&
            new Date(t.due_date) < now &&
            String(t.status || "").toLowerCase() !== "employee_resolved"
        ).length;
        this.checklistCounts.total = openTasks.length;
        this.checklistCounts.overdue = overdue;
      } catch (error) {
        console.error("Eroare la obținerea numerelor de checklist:", error);
        this.checklistCounts.total = null;
        this.checklistCounts.overdue = null;
      } finally {
        this.checklistCounts.loading = false;
      }
    },
    handleBulkCardClick() {
      if (this.employeesLoading) {
        return;
      }
      this.openBulkTimesheetDialog();
    },
    handleDownloadCardClick() {
      if (this.employeesLoading) {
        return;
      }
      this.openBulkDownloadDialog();
    },
    async fetchCompanySettings() {
      if (!this.companyId) return;
      this.companySettings.loading = true;
      try {
        const companyRequest = axios.get(`/candidati/companies/${this.companyId}`);
        const licenseRequest = this.allowCompanyEdit
          ? axios.get(`/companies/${this.companyId}/license`).catch((error) => {
              if (error?.response?.status === 404) {
                return { data: null };
              }
              throw error;
            })
          : Promise.resolve({ data: null });
        const [companyResponse, licenseResponse] = await Promise.all([companyRequest, licenseRequest]);
        this.applyCompanyProfileData(companyResponse?.data || null, licenseResponse?.data || null);
      } catch (error) {
        console.error("Eroare la obținerea setărilor companiei:", error);
        this.companySettings.pdfrequest = false;
        this.companySettings.email_notifications_disabled = false;
        this.companyMeta.name = "";
        this.companyMeta.city = "";
        this.companyLicense = null;
        this.companyProfileForm = createEmptyCompanyForm({
          packageKey: this.getDefaultLicensePackageKey(),
        });
      } finally {
        this.companySettings.loading = false;
      }
    },
    openSettingsDialog() {
      this.settingsDialog = true;
    },
    async openCompanyEditDialog() {
      if (!this.allowCompanyEdit || !this.companyId) return;
      this.companyEditError = "";
      try {
        await this.ensureLicensePackagesLoaded();
        this.companyEditDialog = true;
      } catch (error) {
        this.statusSnackbar = {
          open: true,
          color: "error",
          message: this.$t("companies.messages.loadPackagesError"),
        };
      }
    },
    openBonusesPage() {
      if (this.employeesLoading) return;
      this.bonusesPage = true;
    },
    async saveCompanySettings() {
      if (!this.companyId) return;
      this.companySettings.loading = true;
      try {
        await axios.put(`/candidati/companies/${this.companyId}`, {
          pdfrequest: this.companySettings.pdfrequest ? 1 : 0,
          email_notifications_disabled: this.companySettings.email_notifications_disabled ? 1 : 0,
        });
        this.statusSnackbar = {
          open: true,
          color: "success",
          message: "Setările companiei au fost salvate.",
        };
        this.settingsDialog = false;
      } catch (error) {
        console.error("Eroare la salvarea setărilor companiei:", error);
        this.statusSnackbar = {
          open: true,
          color: "error",
          message: "Nu s-au putut salva setările companiei.",
        };
      } finally {
        this.companySettings.loading = false;
      }
    },
    async saveCompanyProfile(formSnapshot) {
      if (!this.companyId) return;
      this.companyEditLoading = true;
      this.companyEditError = "";
      let companyUpdated = false;
      try {
        const licensePayload = buildLicensePayload(formSnapshot, {
          defaultPackageKey: this.getDefaultLicensePackageKey(),
        });
        const selectedPackage =
          this.availableLicensePackages.find(
            (pkg) => String(pkg?.key || "").trim().toLowerCase() === licensePayload.package_key
          ) || null;
        await axios.put(`/candidati/companies/${this.companyId}`, buildCompanyPayload(formSnapshot));
        companyUpdated = true;
        await axios.put(`/companies/${this.companyId}/license`, licensePayload);
        this.applyCompanyProfileData(
          {
            ...formSnapshot,
            id: this.companyId,
          },
          {
            ...(selectedPackage || {}),
            key: selectedPackage?.key || licensePayload.package_key,
            package_key: licensePayload.package_key,
            expires_at: licensePayload.expires_at,
          }
        );
        this.companyEditDialog = false;
        this.$emit("company-updated", {
          companyId: this.companyId,
          companyName: formSnapshot.company_name || this.companyMeta.name,
        });
        this.statusSnackbar = {
          open: true,
          color: "success",
          message: this.$t("companies.messages.updateSuccess"),
        };
      } catch (error) {
        console.error("Eroare la actualizarea companiei din CompanyDetails:", error);
        if (companyUpdated) {
          this.companyEditDialog = false;
          this.$emit("company-updated", {
            companyId: this.companyId,
            companyName: formSnapshot.company_name || this.companyMeta.name,
          });
          this.statusSnackbar = {
            open: true,
            color: "warning",
            message: this.$t("companies.messages.licensePartial"),
          };
          return;
        }
        this.companyEditError = this.$t("companies.messages.saveError");
      } finally {
        this.companyEditLoading = false;
      }
    },
    openBulkTimesheetDialog() {
      if (!this.employees.length) {
        this.statusSnackbar = {
          open: true,
          color: "info",
          message: "Nu există angajați disponibili pentru această companie.",
        };
        return;
      }

      const defaults = buildDefaultFillRange();
      this.bulkTimesheetDialog.open = true;
      this.bulkTimesheetDialog.loading = false;
      this.bulkTimesheetDialog.error = null;
      this.bulkTimesheetDialog.start_date = defaults.start_date;
      this.bulkTimesheetDialog.end_date = defaults.end_date;
      this.bulkTimesheetDialog.results = [];
      this.bulkTimesheetActiveEdge = "start";
      this.bulkTimesheetRangeMenu = false;
      if (this.selectAllEmployees) {
        this.selectedEmployeeIds = this.employees.map((employee) => employee.id);
      }
    },
    openBulkDownloadDialog() {
      if (!this.employees.length) {
        this.statusSnackbar = {
          open: true,
          color: "info",
          message: "Nu există angajați disponibili pentru această companie.",
        };
        return;
      }

      const defaults = buildDefaultFillRange();
      this.bulkDownloadDialog.open = true;
      this.bulkDownloadDialog.loading = false;
      this.bulkDownloadDialog.error = null;
      this.bulkDownloadDialog.notice = "";
      this.bulkDownloadDialog.start_date = defaults.start_date;
      this.bulkDownloadDialog.end_date = defaults.end_date;
      this.bulkDownloadDialog.format = "pdf";
      this.bulkDownloadActiveEdge = "start";
      this.bulkDownloadRangeMenu = false;
      if (this.downloadSelectAll) {
        this.selectedDownloadEmployeeIds = this.employees.map((employee) => employee.id);
      }
    },
    async fetchEmployees() {
      if (!this.companyId) return;
      this.employeesLoading = true;
      try {
        const { data } = await axios.get("/candidati/employees", {
          params: { company_id: this.companyId },
        });
        const list = Array.isArray(data) ? data : [];
        this.employees = list.sort((a, b) => a.first_name.localeCompare(b.first_name));
      } catch (error) {
        console.error("Eroare la obținerea angajaților companiei:", error);
        this.statusSnackbar = {
          open: true,
          color: "error",
          message: "Nu s-a putut încărca lista de angajați.",
        };
      } finally {
        this.employeesLoading = false;
      }
    },
    onBulkTimesheetRangeChange(value) {
      const selection = normalizeRangeSelection(value);
      const targetEdge = this.bulkTimesheetActiveEdge || "start";
      const currentStart = this.bulkTimesheetDialog.start_date;
      const currentEnd = this.bulkTimesheetDialog.end_date;

      if (targetEdge === "end") {
        const candidate = selection.hasEnd
          ? selection.end
          : selection.hasStart
          ? selection.start
          : "";

        if (candidate) {
          if (currentStart && currentStart > candidate) {
            this.bulkTimesheetDialog.start_date = candidate;
            this.bulkTimesheetDialog.end_date = currentStart;
          } else {
            this.bulkTimesheetDialog.end_date = candidate;
            if (!currentStart && selection.hasStart) {
              this.bulkTimesheetDialog.start_date = selection.start;
            }
          }

          if (this.bulkTimesheetDialog.start_date && this.bulkTimesheetDialog.end_date) {
            this.bulkTimesheetRangeMenu = false;
            this.bulkTimesheetActiveEdge = "start";
          }
        }
        return;
      }

      const candidateStart = selection.hasStart
        ? selection.start
        : selection.hasEnd
        ? selection.end
        : "";

      if (candidateStart) {
        if (selection.hasStart && selection.hasEnd) {
          const nextStart = selection.start;
          const nextEnd = selection.end;
          if (nextStart <= nextEnd) {
            this.bulkTimesheetDialog.start_date = nextStart;
            this.bulkTimesheetDialog.end_date = nextEnd;
          } else {
            this.bulkTimesheetDialog.start_date = nextEnd;
            this.bulkTimesheetDialog.end_date = nextStart;
          }
          this.bulkTimesheetRangeMenu = false;
          this.bulkTimesheetActiveEdge = "start";
        } else {
          this.bulkTimesheetDialog.start_date = candidateStart;
          if (currentEnd && currentEnd < candidateStart) {
            this.bulkTimesheetDialog.end_date = candidateStart;
          }
          if (!selection.hasEnd) {
            this.bulkTimesheetActiveEdge = "end";
          }
        }
      }
    },
    onBulkDownloadRangeChange(value) {
      const selection = normalizeRangeSelection(value);
      const targetEdge = this.bulkDownloadActiveEdge || "start";
      const currentStart = this.bulkDownloadDialog.start_date;
      const currentEnd = this.bulkDownloadDialog.end_date;

      if (targetEdge === "end") {
        const candidate = selection.hasEnd
          ? selection.end
          : selection.hasStart
          ? selection.start
          : "";

        if (candidate) {
          if (currentStart && currentStart > candidate) {
            this.bulkDownloadDialog.start_date = candidate;
            this.bulkDownloadDialog.end_date = currentStart;
          } else {
            this.bulkDownloadDialog.end_date = candidate;
            if (!currentStart && selection.hasStart) {
              this.bulkDownloadDialog.start_date = selection.start;
            }
          }

          if (this.bulkDownloadDialog.start_date && this.bulkDownloadDialog.end_date) {
            this.bulkDownloadRangeMenu = false;
            this.bulkDownloadActiveEdge = "start";
          }
        }
        return;
      }

      const candidateStart = selection.hasStart
        ? selection.start
        : selection.hasEnd
        ? selection.end
        : "";

      if (candidateStart) {
        if (selection.hasStart && selection.hasEnd) {
          const nextStart = selection.start;
          const nextEnd = selection.end;
          if (nextStart <= nextEnd) {
            this.bulkDownloadDialog.start_date = nextStart;
            this.bulkDownloadDialog.end_date = nextEnd;
          } else {
            this.bulkDownloadDialog.start_date = nextEnd;
            this.bulkDownloadDialog.end_date = nextStart;
          }
          this.bulkDownloadRangeMenu = false;
          this.bulkDownloadActiveEdge = "start";
        } else {
          this.bulkDownloadDialog.start_date = candidateStart;
          if (currentEnd && currentEnd < candidateStart) {
            this.bulkDownloadDialog.end_date = candidateStart;
          }
          if (!selection.hasEnd) {
            this.bulkDownloadActiveEdge = "end";
          }
        }
      }
    },
    handleBulkTimesheetPointerDown(edge) {
      this.bulkTimesheetActiveEdge = edge;
    },
    handleBulkTimesheetFieldFocus(edge) {
      this.bulkTimesheetActiveEdge = edge;
    },
    handleBulkTimesheetKeyboardOpen(edge) {
      this.bulkTimesheetActiveEdge = edge;
      this.bulkTimesheetRangeMenu = true;
    },
    handleBulkTimesheetControlClick(edge, event, menuProps) {
      if (event?.preventDefault) {
        event.preventDefault();
      }
      if (event?.stopPropagation) {
        event.stopPropagation();
      }
      this.bulkTimesheetActiveEdge = edge;
      this.bulkTimesheetRangeMenu = true;
      if (menuProps && typeof menuProps.onClick === "function") {
        menuProps.onClick(event);
      }
    },
    handleBulkDownloadPointerDown(edge) {
      this.bulkDownloadActiveEdge = edge;
    },
    handleBulkDownloadFieldFocus(edge) {
      this.bulkDownloadActiveEdge = edge;
    },
    handleBulkDownloadKeyboardOpen(edge) {
      this.bulkDownloadActiveEdge = edge;
      this.bulkDownloadRangeMenu = true;
    },
    handleBulkDownloadControlClick(edge, event, menuProps) {
      if (event?.preventDefault) {
        event.preventDefault();
      }
      if (event?.stopPropagation) {
        event.stopPropagation();
      }
      this.bulkDownloadActiveEdge = edge;
      this.bulkDownloadRangeMenu = true;
      if (menuProps && typeof menuProps.onClick === "function") {
        menuProps.onClick(event);
      }
    },
    async confirmBulkTimesheetFill() {
      const startIso = this.bulkTimesheetDialog.start_date;
      const endIso = this.bulkTimesheetDialog.end_date;
      const startDate = toDateObject(startIso);
      const endDate = toDateObject(endIso);

      if (!startDate || !endDate) {
        this.bulkTimesheetDialog.error = "Perioada selectată este invalidă.";
        return;
      }

      const selectedIds = Array.isArray(this.selectedEmployeeIds)
        ? this.selectedEmployeeIds.filter(Boolean)
        : [];

      if (!selectedIds.length) {
        this.bulkTimesheetDialog.error = "Selectează cel puțin un angajat.";
        return;
      }

      let rangeStart = startDate;
      let rangeEnd = endDate;
      if (rangeEnd < rangeStart) {
        const temp = rangeStart;
        rangeStart = rangeEnd;
        rangeEnd = temp;
      }

      const normalizedStart = formatAsIsoDate(rangeStart);
      const normalizedEnd = formatAsIsoDate(rangeEnd);
      this.bulkTimesheetDialog.start_date = normalizedStart;
      this.bulkTimesheetDialog.end_date = normalizedEnd;
      this.bulkTimesheetDialog.loading = true;
      this.bulkTimesheetDialog.error = null;
      this.bulkTimesheetDialog.results = [];
      this.bulkTimesheetRangeMenu = false;

      const results = [];
      let successCount = 0;

      for (const employeeId of selectedIds) {
        const employee = this.employees.find((item) => item.id === employeeId);
        const employeeName = employee
          ? `${employee.first_name || ""} ${employee.last_name || ""}`.trim() || `Angajat #${employeeId}`
          : `Angajat #${employeeId}`;

        try {
          const payload = {
            employee_id: employeeId,
            start_date: normalizedStart,
            end_date: normalizedEnd,
          };
          const { data } = await axios.post("/timesheet/fill-range", payload);
          const inserted = Number(data?.inserted_events || 0);
          const filledDays = Number(data?.filled_days || 0);
          const message = inserted
            ? `Succes: ${inserted} evenimente adăugate pe ${filledDays} zile.`
            : "Nu au fost găsite evenimente lipsă.";

          results.push({
            id: employeeId,
            name: employeeName,
            success: true,
            message,
          });
          successCount += 1;
        } catch (error) {
          console.error(`Eroare completare pontaj pentru ${employeeName}:`, error);
          const message = error?.response?.data?.error || "Nu s-a putut completa pontajul.";
          results.push({
            id: employeeId,
            name: employeeName,
            success: false,
            message,
          });
        }
      }

      this.bulkTimesheetDialog.results = results;

      const totalCount = results.length;
      const failureCount = results.filter((result) => !result.success).length;
      const allSucceeded = failureCount === 0;
      const anySucceeded = successCount > 0;

      let snackbarMessage = "";
      let snackbarColor = "info";

      if (allSucceeded) {
        snackbarMessage = `Pontaj completat cu succes pentru ${totalCount} angajați.`;
        snackbarColor = "success";
      } else if (anySucceeded) {
        snackbarMessage = `Pontaj completat pentru ${successCount} angajați, ${failureCount} au eșuat.`;
        snackbarColor = "warning";
      } else {
        snackbarMessage = "Nu s-a putut completa pontajul pentru angajații selectați.";
        snackbarColor = "error";
      }

      this.statusSnackbar = {
        open: true,
        color: snackbarColor,
        message: snackbarMessage,
      };

      this.bulkTimesheetDialog.loading = false;
    },
    async confirmBulkDownload() {
      const startIso = this.bulkDownloadDialog.start_date;
      const endIso = this.bulkDownloadDialog.end_date;
      const startDate = toDateObject(startIso);
      const endDate = toDateObject(endIso);

      if (!startDate || !endDate) {
        this.bulkDownloadDialog.error = this.$t("companyDetailsPage.bulkDownloadDialog.errors.invalidRange");
        return;
      }

      let rangeStart = startDate;
      let rangeEnd = endDate;
      if (rangeEnd < rangeStart) {
        const temp = rangeStart;
        rangeStart = rangeEnd;
        rangeEnd = temp;
      }

      const normalizedStart = formatAsIsoDate(rangeStart);
      const normalizedEnd = formatAsIsoDate(rangeEnd);
      this.bulkDownloadDialog.start_date = normalizedStart;
      this.bulkDownloadDialog.end_date = normalizedEnd;

      const selectedIds = Array.isArray(this.selectedDownloadEmployeeIds)
        ? this.selectedDownloadEmployeeIds.filter(Boolean)
        : [];
      if (!selectedIds.length) {
        this.bulkDownloadDialog.error = this.$t("companyDetailsPage.bulkDownloadDialog.errors.noEmployees");
        return;
      }

      this.bulkDownloadDialog.loading = true;
      this.bulkDownloadDialog.error = null;
      this.bulkDownloadDialog.notice = "";
      this.bulkDownloadRangeMenu = false;

      try {
        const payload = {
          company_id: this.companyId,
          employee_ids: selectedIds,
          start_date: normalizedStart,
          end_date: normalizedEnd,
          format: this.bulkDownloadDialog.format,
        };
        const response = await axios.post("/timesheet/export-bulk", payload, {
          responseType: "blob",
        });
        const contentType = response.headers["content-type"] || "application/octet-stream";
        const blob = new Blob([response.data], { type: contentType });
        let filename = `pontaj-companie-${this.companyId}-${normalizedStart}-${normalizedEnd}.${this.bulkDownloadDialog.format === "xlsx" ? "xlsx" : "pdf"}`;
        const disposition = response.headers["content-disposition"];
        if (disposition) {
          const match = disposition.match(/filename="?([^";]+)"?/);
          if (match && match[1]) {
            filename = match[1];
          }
        }

        const link = document.createElement("a");
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.bulkDownloadDialog.notice = this.$t("companyDetailsPage.bulkDownloadDialog.notice", {
          format: this.bulkDownloadDialog.format.toUpperCase(),
          count: selectedIds.length,
        });
        this.statusSnackbar = {
          open: true,
          color: "success",
          message: `Pontaj descărcat pentru ${selectedIds.length} angajați.`,
        };
      } catch (error) {
        console.error("Eroare export pontaj:", error);
        const message =
          error?.response?.data?.error ||
          "Nu s-a putut descărca raportul.";
        this.bulkDownloadDialog.error = message;
        this.statusSnackbar = {
          open: true,
          color: "error",
          message: "Descărcarea raportului a eșuat.",
        };
      } finally {
        this.bulkDownloadDialog.loading = false;
      }
    },
  },
});
</script>

<style scoped>
.company-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.details-grid__col {
  display: flex;
  justify-content: center;
}

.details-card {
  width: 100%;
  max-width: 400px;
  border-radius: var(--app-radius-base);
  cursor: pointer;
  transition: box-shadow 0.2s ease;
  position: relative;
  overflow: hidden;
}

.details-card:hover {
  box-shadow: 0 4px 14px rgba(var(--v-theme-on-surface), 0.08);
}

.details-card--loading {
  cursor: progress;
  pointer-events: none;
}

.settings-row {
  margin-bottom: 6px;
}

.details-card__loader {
  position: absolute;
  inset: auto 0 0 0;
}

.details-card__title {
  font-weight: 600;
  font-size: 18px;
  justify-content: center;
}

.details-card__text {
  color: rgba(var(--v-theme-on-surface), 0.70);
}

.counts-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 4px;
}

.counts-chip {
  border-radius: var(--app-radius-base);
  font-weight: 600;
  letter-spacing: 0.1px;
  background: rgba(var(--v-theme-on-surface), 0.03);
}

.counts-chip--total {
  color: var(--app-text-strong);
  border-color: rgba(var(--v-theme-on-surface), 0.12);
}

.counts-chip--overdue {
  color: rgb(var(--v-theme-error));
  border-color: rgba(var(--v-theme-error), 0.30);
  background: rgba(var(--v-theme-error), 0.06);
}

.counts-chip--overdue-zero {
  color: var(--app-text-strong);
  border-color: rgba(var(--v-theme-on-surface), 0.12);
  background: rgba(var(--v-theme-on-surface), 0.03);
}

.counts-label {
  margin-right: 4px;
}

.counts-value {
  font-weight: 700;
}

.employee-list {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: var(--app-radius-base);
}
</style>
